import { SECTIONS, SIDE_GROUPS, type SideItem } from 'components/HomepageV3/data/demoData';
import type { FilterSelection } from 'components/HomepageV3/components/FilterSidebar';
import { useSearchParams } from 'next/navigation';

/**
 * Build a clean `/explore` route, optionally carrying one extra query key.
 * No `se3` param — editorial Explore is chosen server-side from the flag
 * (see app/explore/page.jsx), so production URLs stay clean.
 */
const SE3_ROUTE_RE = /^\/(?:$|explore(?:\/|$)|private(?:\/|$)|offering(?:\/|$))/;
const DISCOVER_HOST_RE = /^(?:www\.|stage\.)?startengine\.com$/i;
/** Hydration-safe access to the current query string for SE3 QA links. */
export const useSe3QaSearch = (): string => {
  const params = useSearchParams();
  const search = params.toString();
  return search ? `?${search}` : '';
};

/**
 * Keep explicit editorial QA/demo parameters while navigating between SE3
 * surfaces. PostHog-bucketed production visitors intentionally keep clean URLs.
 */
export const withSe3QaOverride = (href: string, currentSearch?: string): string => {
  const search = currentSearch ?? (typeof window !== 'undefined' ? window.location.search : '');
  const sourceParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const sourceSe3 = sourceParams.get('se3');
  const sourceSe3Enabled =
    sourceParams.has('se3') && (sourceSe3 === '' || sourceSe3 === '1' || sourceSe3 === 'true');
  const sourceV3Enabled = sourceParams.get('v3') === 'true';
  const demoMode = sourceParams.get('restrictedOfferingsDemo');
  const validDemoMode = demoMode === 'locked' || demoMode === 'allowlisted';
  const privateClientPreview = sourceParams.get('privateClientPreview') === 'true';
  const sourceTombstoneVariant = sourceParams.get('tombstone_variant')?.trim().toLowerCase();
  const preserveTombstoneVariant =
    sourceTombstoneVariant === 'a' ||
    sourceTombstoneVariant === 'b' ||
    sourceTombstoneVariant === 'c' ||
    sourceTombstoneVariant === 'control' ||
    sourceTombstoneVariant === 'marketplace' ||
    sourceTombstoneVariant === 'marketplace-overlay' ||
    sourceTombstoneVariant === 'hybrid';
  if (
    !sourceSe3Enabled &&
    !sourceV3Enabled &&
    !validDemoMode &&
    !privateClientPreview &&
    !preserveTombstoneVariant
  ) {
    return href;
  }

  const isAbsolute = /^https?:\/\//i.test(href);
  if (!isAbsolute && (!href.startsWith('/') || href.startsWith('//'))) return href;

  try {
    const url = new URL(href, 'https://www.startengine.com');
    if (isAbsolute && !DISCOVER_HOST_RE.test(url.hostname)) return href;
    if (!SE3_ROUTE_RE.test(url.pathname)) return href;
    if (sourceSe3Enabled && !url.searchParams.has('se3')) {
      url.searchParams.set('se3', '1');
    } else if (sourceV3Enabled && !url.searchParams.has('se3') && !url.searchParams.has('v3')) {
      url.searchParams.set('v3', 'true');
    }
    if (
      validDemoMode &&
      (url.pathname === '/' || url.pathname.startsWith('/explore')) &&
      !url.searchParams.has('restrictedOfferingsDemo')
    ) {
      url.searchParams.set('restrictedOfferingsDemo', demoMode);
    }
    if (
      privateClientPreview &&
      (url.pathname === '/' || url.pathname.startsWith('/explore')) &&
      !url.searchParams.has('privateClientPreview')
    ) {
      url.searchParams.set('privateClientPreview', 'true');
    }
    if (preserveTombstoneVariant && !url.searchParams.has('tombstone_variant')) {
      url.searchParams.set('tombstone_variant', sourceTombstoneVariant);
    }
    return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};

const buildSe3ExploreQuery = (
  key?: string,
  value?: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => {
  const params = new URLSearchParams();
  const trimmed = (value ?? '').trim();
  if (key && trimmed) {
    params.set(key, trimmed);
  }
  const qs = params.toString();
  const href = qs ? `/explore?${qs}` : '/explore';
  return withSe3QaOverride(href, currentSearch) as '/explore' | `/explore?${string}`;
};

export const buildSe3ExploreRoute = (
  value?: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => buildSe3ExploreQuery('search', value, currentSearch);

export const buildSe3ExploreSectionRoute = (
  section: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => buildSe3ExploreQuery('section', section, currentSearch);

export const buildSe3ExploreFilterRoute = (
  filter: string,
  currentSearch?: string,
): '/explore' | `/explore?${string}` => buildSe3ExploreQuery('filter', filter, currentSearch);

export const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/**
 * Resolve a `section=` / `filter=` URL slug to the matching sidebar selection so
 * deep links from "View all" shelves and footer filters open the requested
 * shelf/filter instead of the generic browse view. Returns null when no sidebar
 * item matches (e.g. the `private`/`crowdfunding` footer filters, which the
 * Explore page maps to the sidebar `view` instead).
 */
export const resolveSelectionFromSlug = (
  slug?: string | null,
  additionalBrowseItems: SideItem[] = [],
): FilterSelection | null => {
  const target = (slug ?? '').trim();
  if (!target) return null;
  const additionalItem = additionalBrowseItems.find(
    (item) => slugify(item.key) === target || slugify(item.label) === target,
  );
  if (additionalItem) return { group: 'Browse by', item: additionalItem };
  for (const group of SIDE_GROUPS) {
    const item = group.items.find(
      (it) => slugify(it.key) === target || slugify(it.label) === target,
    );
    if (item) return { group: group.label, item };
  }
  const section = SECTIONS.find((item) => slugify(item.title) === target);
  if (section) {
    return {
      group: 'Browse by',
      item: { label: section.title, type: 'browse', key: section.title },
    };
  }
  return null;
};
